import { styled } from '@linaria/react';
import { ActionIcon, Loader, Select, TextInput, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useFloraUserPreferences,
  useUpdateFloraUserPreferences,
} from '../../../../api/agents-client/agent-client.hooks';
import type { FloraUserPreferences, ImportantObject } from '../../../../api/agents-client/agent-client.type';
import { useInitiatives } from '../../../../api/initiative-client/initiative-client.hooks';
import { useGoalsMinimal, useOutcomesMinimal } from '../../../../api/strategy-client/strategy-client.hooks';
import { useSimpleEpics } from '../../../../api/tasks-client/tasks-client.hooks';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { Button } from '../../../../ui-library/button/button';
import { Divider } from '../../../../ui-library/divider/divider';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import {
  getMentionTypeColor,
  getMentionTypeLabel,
  getMentionTypeLabelPlural,
} from '../../../flora/components/flora-mention/flora-mention.helpers';
import {
  CATEGORY_ICONS,
  CATEGORY_ORDER,
  type MentionItem,
  type MentionType,
} from '../../../flora/components/flora-mention/flora-mention.type';
import { AgentViewHeader } from '../agent-view-header';
import { INPUT_STYLES, ROLE_OPTIONS, SELECT_STYLES } from './agent-preferences.type';

const MAX_IMPORTANT_OBJECTS = 5;
const MAX_NICKNAME_LENGTH = 30;
const MAX_PREFERENCES_LENGTH = 300;

const { minHeight: _mh, ...textareaInputBase } = INPUT_STYLES.input;
const TEXTAREA_STYLES = {
  input: {
    ...textareaInputBase,
    paddingTop: 12,
    paddingBottom: 12,
  },
};

interface AgentPreferencesFormProps {
  /** Render inside the settings layout: compact heading, no page padding or scrolling. */
  embedded?: boolean;
  /** Shown before the heading when embedded, e.g. the settings section's icon tile. */
  leading?: ReactNode;
}

/**
 * User-authored Flora profile form: nickname, role, free-text general
 * preferences, and the "important objects" Flora prioritizes. Renders the
 * "Your Preferences" section of the preferences page; owns its own fetch,
 * dirty-tracking, and save logic.
 *
 * @param {AgentPreferencesFormProps} props - Whether the view renders inside the settings layout, and what leads its heading there
 * @returns {JSX.Element} The preferences form section.
 */
export function AgentPreferencesForm({ embedded = false, leading }: AgentPreferencesFormProps = {}) {
  const { t } = useTranslation();
  const { preferences, isLoading, error } = useFloraUserPreferences();
  const { mutate: updatePreferences, isPending: isSaving } = useUpdateFloraUserPreferences();

  const [preferredName, setPreferredName] = useState('');
  const [role, setRole] = useState('');
  const [generalPreferences, setGeneralPreferences] = useState('');
  const [importantObjects, setImportantObjects] = useState<ImportantObject[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<MentionType | null>(null);

  // Snapshot of the values the form was last synced FROM (via hydration or a
  // successful save) — NOT the live `preferences`. Comparing against the live
  // value would make isDirty flip true for one render every time an external
  // update arrives (local state hasn't caught up yet), which would then block
  // the very hydration effect that's supposed to sync it, permanently
  // stranding the form on stale data.
  //
  // Held in STATE (not a ref) so that re-pointing the baseline after a save
  // re-runs the isDirty memo and disables the header Save button — a ref
  // mutation wouldn't retrigger the memo, leaving Save enabled post-save.
  const [syncedPreferences, setSyncedPreferences] = useState<FloraUserPreferences | null>(null);

  // Dirty tracking — compare current state vs the last-synced snapshot
  const isDirty = useMemo(() => {
    const fetchedName = syncedPreferences?.preferred_name || '';
    const fetchedRole = syncedPreferences?.role || '';
    const fetchedGeneralPrefs = syncedPreferences?.general_preferences || '';
    const fetchedObjects = syncedPreferences?.important_objects || [];
    if (preferredName !== fetchedName) return true;
    if (role !== fetchedRole) return true;
    if (generalPreferences !== fetchedGeneralPrefs) return true;
    if (importantObjects.length !== fetchedObjects.length) return true;

    const currentIds = importantObjects.map((object) => object.id).sort();
    const fetchedIds = fetchedObjects.map((object) => object.id).sort();
    return currentIds.join(',') !== fetchedIds.join(',');
  }, [preferredName, role, generalPreferences, importantObjects, syncedPreferences]);

  // Initialize form state from fetched preferences — but only while the form
  // has no unsaved edits, so a background refetch (e.g. from the WS cache
  // invalidation when Flora writes /memories/preferences.md) can't
  // clobber what the user is actively typing. On first mount, local state and
  // the (null) snapshot are both empty, so isDirty is false and hydration
  // always runs.
  // biome-ignore lint/correctness/useExhaustiveDependencies(isDirty): only `preferences` changing should re-run this — isDirty is read fresh from the same render, not a retrigger condition
  useEffect(() => {
    if (preferences && !isDirty) {
      setPreferredName(preferences.preferred_name || '');
      setRole(preferences.role || '');
      setGeneralPreferences(preferences.general_preferences || '');
      setImportantObjects(preferences.important_objects || []);
      setSyncedPreferences(preferences);
    }
  }, [preferences]);

  // Fetch mentionable items for object search
  const organization = useGlobalStore((state) => state.organization);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const portfolios = useGlobalStore((state) => state.portfolios);
  const teams = useGlobalStore((state) => state.teams);
  const { goals } = useGoalsMinimal();
  const { outcomes } = useOutcomesMinimal();
  const { initiatives } = useInitiatives(portfolio?.id);
  const { epics } = useSimpleEpics();

  const allItems = useMemo((): MentionItem[] => {
    const items: MentionItem[] = [];

    if (organization?.id) {
      portfolios
        .filter((p) => p.organization.id === organization.id)
        .forEach((p) => {
          items.push({ id: p.id, name: p.name, type: 'portfolio' });
        });
    }

    if (teams) {
      teams.forEach((project) => {
        items.push({ id: project.id, name: project.name, type: 'project' });
        project.subprojects?.forEach((subproject) => {
          items.push({ id: subproject.id, name: subproject.name, type: 'board', parentName: project.name });
        });
      });
    }

    goals.forEach((goal) => {
      items.push({ id: goal.id, name: goal.name, type: 'goal' });
    });

    outcomes.forEach((outcome) => {
      items.push({ id: outcome.id, name: outcome.name, type: 'outcome' });
    });

    initiatives?.forEach((initiative) => {
      items.push({ id: initiative.id, name: initiative.name, type: 'initiative' });
    });

    epics?.forEach((epic) => {
      items.push({ id: epic.id, name: epic.name, type: 'epic' });
    });

    return items;
  }, [organization?.id, portfolios, teams, goals, outcomes, initiatives, epics]);

  // Available items excluding already-selected
  const availableItems = useMemo(() => {
    const selectedIds = new Set(importantObjects.map((object) => object.id));
    return allItems.filter((item) => !selectedIds.has(item.id));
  }, [allItems, importantObjects]);

  // Available categories with counts (for category view)
  const availableCategories = useMemo(() => {
    const grouped: { type: MentionType; count: number }[] = [];
    for (const type of CATEGORY_ORDER) {
      const count = availableItems.filter((item) => item.type === type).length;
      if (count > 0) {
        grouped.push({ type, count });
      }
    }
    return grouped;
  }, [availableItems]);

  // Items for the selected category, filtered by search
  const categoryItems = useMemo(() => {
    if (!selectedCategory) return [];
    const items = availableItems.filter((item) => item.type === selectedCategory);
    if (!searchQuery) return items;
    const lowerQuery = searchQuery.toLowerCase();
    return items.filter(
      (item) => item.name.toLowerCase().includes(lowerQuery) || item.parentName?.toLowerCase().includes(lowerQuery),
    );
  }, [availableItems, selectedCategory, searchQuery]);

  // Search results across all categories (shown in category view when searching)
  const searchResults = useMemo(() => {
    if (!searchQuery) return [];
    const lowerQuery = searchQuery.toLowerCase();
    return availableItems.filter(
      (item) =>
        item.name.toLowerCase().includes(lowerQuery) ||
        item.type.toLowerCase().includes(lowerQuery) ||
        item.parentName?.toLowerCase().includes(lowerQuery),
    );
  }, [availableItems, searchQuery]);

  const hasDropdownContent = selectedCategory
    ? categoryItems.length > 0
    : searchResults.length > 0 || availableCategories.length > 0;

  const isAtObjectLimit = importantObjects.length >= MAX_IMPORTANT_OBJECTS;

  const handleSelectItem = useCallback(
    (item: MentionItem) => {
      if (isAtObjectLimit) return;
      setImportantObjects((previous) => [...previous, { id: item.id, name: item.name, object_type: item.type }]);
    },
    [isAtObjectLimit],
  );

  const handleCategorySelect = useCallback((type: MentionType) => {
    setSelectedCategory(type);
    setSearchQuery('');
  }, []);

  const handleBackToCategories = useCallback(() => {
    setSelectedCategory(null);
    setSearchQuery('');
  }, []);

  const handleRemoveObject = useCallback((objectId: string) => {
    setImportantObjects((previous) => previous.filter((object) => object.id !== objectId));
  }, []);

  const handleSave = useCallback(() => {
    updatePreferences(
      {
        preferred_name: preferredName || null,
        role: role || null,
        general_preferences: generalPreferences || null,
        important_objects: importantObjects,
      },
      {
        onSuccess: () => {
          // Mark what was just saved as the new synced baseline immediately —
          // don't wait for the refetch to round-trip — so isDirty (and the
          // Save button) clear right away instead of staying stuck dirty.
          setSyncedPreferences({
            preferred_name: preferredName || null,
            role: role || null,
            general_preferences: generalPreferences || null,
            important_objects: importantObjects,
          });
          notifications.show({
            message: 'Preferences saved',
            color: 'green',
          });
        },
        onError: () => {
          notifications.show({
            message: 'Failed to save preferences',
            color: 'red',
          });
        },
      },
    );
  }, [preferredName, role, generalPreferences, importantObjects, updatePreferences]);

  const handleDropdownClose = useCallback(() => {
    setTimeout(() => {
      setIsSearchOpen(false);
      setSelectedCategory(null);
      setSearchQuery('');
    }, 200);
  }, []);

  if (isLoading) {
    return (
      <Container data-embedded={embedded || undefined}>
        <CenterWrap>
          <AgentViewHeader
            title={embedded ? 'Profile' : 'Your Preferences'}
            variant={embedded ? 'settings-page' : 'page'}
            leading={embedded ? leading : undefined}
          />
          <Content>
            <LoadingState>
              <Loader size="sm" color="var(--secondary-base)" />
            </LoadingState>
          </Content>
        </CenterWrap>
      </Container>
    );
  }

  if (error) {
    return (
      <Container data-embedded={embedded || undefined}>
        <CenterWrap>
          <AgentViewHeader
            title={embedded ? 'Profile' : 'Your Preferences'}
            variant={embedded ? 'settings-page' : 'page'}
            leading={embedded ? leading : undefined}
          />
          <Content>
            <EmptyState>
              <Text size="regular" color="var(--ink-lighter)">
                Failed to load preferences.
              </Text>
            </EmptyState>
          </Content>
        </CenterWrap>
      </Container>
    );
  }

  return (
    <Container data-embedded={embedded || undefined}>
      <CenterWrap>
        <AgentViewHeader
          title={embedded ? 'Profile' : 'Your Preferences'}
          variant={embedded ? 'settings-page' : 'page'}
          leading={embedded ? leading : undefined}
          rightSlot={
            <Button variant="primary" size="sm" onClick={handleSave} disabled={!isDirty} loading={isSaving}>
              Save
            </Button>
          }
        />

        <Content>
          <FormContent>
            <FormSection>
              <Label>
                <Text size="regular" weight="medium" color="var(--ink-dark)">
                  Nickname
                </Text>
              </Label>
              <TextInput
                placeholder="How should Flora address you?"
                value={preferredName}
                onChange={(event) => setPreferredName(event.currentTarget.value)}
                maxLength={MAX_NICKNAME_LENGTH}
                styles={INPUT_STYLES}
              />
              <CharCounter>
                {preferredName.length}/{MAX_NICKNAME_LENGTH}
              </CharCounter>
            </FormSection>

            <FormSection>
              <Label>
                <Text size="regular" weight="medium" color="var(--ink-dark)">
                  Role
                </Text>
              </Label>
              <Select
                placeholder="Select your role"
                data={ROLE_OPTIONS}
                value={role || null}
                onChange={(value) => setRole(value || '')}
                searchable
                clearable
                rightSectionPointerEvents="all"
                clearButtonProps={{
                  'aria-label': 'Clear role',
                  style: { background: 'transparent', color: 'var(--ink-base)' },
                }}
                comboboxProps={{ withinPortal: false }}
                styles={SELECT_STYLES}
              />
            </FormSection>

            <FormSection>
              <Label>
                <Text size="regular" weight="medium" color="var(--ink-dark)">
                  General Preferences
                </Text>
              </Label>
              <Textarea
                placeholder="Tell Flora about your preferences, communication style, or anything else that helps personalize your experience..."
                value={generalPreferences}
                onChange={(event) => setGeneralPreferences(event.currentTarget.value)}
                maxLength={MAX_PREFERENCES_LENGTH}
                autosize
                minRows={3}
                maxRows={8}
                styles={TEXTAREA_STYLES}
              />
              <CharCounter>
                {generalPreferences.length}/{MAX_PREFERENCES_LENGTH}
              </CharCounter>
            </FormSection>

            <Divider color="var(--sky-light)" style={{ margin: '32px 0' }} />

            <FormSection>
              <Label>
                <Text size="regular" weight="medium" color="var(--ink-dark)">
                  Important Objects
                </Text>
                <Text size="tiny" color="var(--ink-lighter)" style={{ marginTop: 4 }}>
                  {importantObjects.length}/{MAX_IMPORTANT_OBJECTS} selected
                </Text>
              </Label>
              <Text size="small" color="var(--ink-lighter)" style={{ marginBottom: 12 }}>
                These are objects that Flora will prioritize when finding insights on your behalf. For example, if you
                are a Scrum Master, you will want to select the boards that are most important to you.
              </Text>
              <SearchContainer>
                <TextInput
                  placeholder={
                    isAtObjectLimit
                      ? `Maximum of ${MAX_IMPORTANT_OBJECTS} objects reached`
                      : selectedCategory
                        ? `Search ${getMentionTypeLabelPlural(selectedCategory, t)}...`
                        : 'Search for objects...'
                  }
                  value={searchQuery}
                  onChange={(event) => {
                    setSearchQuery(event.currentTarget.value);
                    setIsSearchOpen(true);
                  }}
                  onFocus={() => {
                    if (!isAtObjectLimit) setIsSearchOpen(true);
                  }}
                  onBlur={handleDropdownClose}
                  disabled={isAtObjectLimit}
                  styles={INPUT_STYLES}
                />
                {isSearchOpen && hasDropdownContent && !isAtObjectLimit && (
                  <SearchDropdown>
                    {selectedCategory ? (
                      <>
                        <DropdownHeader>
                          <BackButton
                            onMouseDown={(event) => {
                              event.preventDefault();
                              handleBackToCategories();
                            }}
                          >
                            <Icon name="arrow_back" size={16} color="var(--ink-dark)" style={{ cursor: 'pointer' }} />
                          </BackButton>
                          <Text size="small" weight="bold" color={getMentionTypeColor(selectedCategory)}>
                            {getMentionTypeLabelPlural(selectedCategory, t)}
                          </Text>
                        </DropdownHeader>
                        <DropdownBody>
                          {categoryItems.length === 0 ? (
                            <NoResults>
                              <Text size="small" color="var(--ink-lighter)">
                                No matches found
                              </Text>
                            </NoResults>
                          ) : (
                            categoryItems.map((item) => (
                              <ItemButton
                                key={`${item.type}-${item.id}`}
                                type="button"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => handleSelectItem(item)}
                              >
                                <Icon
                                  name={CATEGORY_ICONS[item.type]}
                                  color={getMentionTypeColor(item.type)}
                                  size={16}
                                  style={{ flexShrink: 0 }}
                                />
                                <ItemContent>
                                  <ItemName>{item.name}</ItemName>
                                  {item.parentName && <ItemParent>{item.parentName}</ItemParent>}
                                </ItemContent>
                              </ItemButton>
                            ))
                          )}
                        </DropdownBody>
                      </>
                    ) : (
                      <>
                        {searchResults.length > 0 && (
                          <DropdownSection>
                            <SectionLabel>
                              <Text size="tiny" weight="bold" color="var(--ink-lighter)">
                                MATCHES
                              </Text>
                            </SectionLabel>
                            {searchResults.slice(0, 8).map((item) => (
                              <ItemButton
                                key={`search-${item.type}-${item.id}`}
                                type="button"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => handleSelectItem(item)}
                              >
                                <Icon
                                  name={CATEGORY_ICONS[item.type]}
                                  color={getMentionTypeColor(item.type)}
                                  size={16}
                                  style={{ flexShrink: 0 }}
                                />
                                <ItemContent>
                                  <ItemName>{item.name}</ItemName>
                                  <ItemParent>
                                    {getMentionTypeLabel(item.type, t)}
                                    {item.parentName && ` · ${item.parentName}`}
                                  </ItemParent>
                                </ItemContent>
                              </ItemButton>
                            ))}
                          </DropdownSection>
                        )}
                        {availableCategories.length > 0 && (
                          <DropdownSection>
                            {searchResults.length > 0 && (
                              <SectionLabel>
                                <Text size="tiny" weight="bold" color="var(--ink-lighter)">
                                  BROWSE BY TYPE
                                </Text>
                              </SectionLabel>
                            )}
                            {availableCategories.map((cat) => {
                              const color = getMentionTypeColor(cat.type);
                              return (
                                <CategoryButton
                                  key={cat.type}
                                  type="button"
                                  onMouseDown={(event) => event.preventDefault()}
                                  onClick={() => handleCategorySelect(cat.type)}
                                >
                                  <CategoryIconBox style={{ backgroundColor: `${color}12` }}>
                                    <Icon name={CATEGORY_ICONS[cat.type]} color={color} size={20} />
                                  </CategoryIconBox>
                                  <CategoryContent>
                                    <Text size="small" weight="bold" color="var(--ink-darkest)">
                                      {getMentionTypeLabelPlural(cat.type, t)}
                                    </Text>
                                    <CategorySubtitle>{cat.count} available</CategorySubtitle>
                                  </CategoryContent>
                                  <Icon
                                    name="chevron_right"
                                    color="var(--ink-lighter)"
                                    size={18}
                                    style={{ flexShrink: 0 }}
                                  />
                                </CategoryButton>
                              );
                            })}
                          </DropdownSection>
                        )}
                      </>
                    )}
                  </SearchDropdown>
                )}
              </SearchContainer>

              {importantObjects.length > 0 && (
                <SelectedObjectsList>
                  {importantObjects.map((object) => {
                    const mentionType = object.object_type as MentionType;
                    const color = getMentionTypeColor(mentionType);
                    return (
                      <SelectedObjectRow key={object.id}>
                        <Icon name={CATEGORY_ICONS[mentionType]} color={color} size={18} style={{ flexShrink: 0 }} />
                        <SelectedObjectInfo>
                          <Text size="small" weight="medium" color="var(--ink-darkest)">
                            {object.name}
                          </Text>
                          <Text size="tiny" color="var(--ink-lighter)">
                            {getMentionTypeLabel(mentionType, t)}
                          </Text>
                        </SelectedObjectInfo>
                        <RemoveButton size="xs" variant="subtle" onClick={() => handleRemoveObject(object.id)}>
                          <Icon name="close" size={14} color="var(--ink-lighter)" style={{ cursor: 'pointer' }} />
                        </RemoveButton>
                      </SelectedObjectRow>
                    );
                  })}
                </SelectedObjectsList>
              )}
            </FormSection>
          </FormContent>
        </Content>
      </CenterWrap>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  padding: 40px 0;
  overflow-y: auto;

  &[data-embedded] {
    height: auto;
    padding: 0;
    overflow: visible;
  }
`;

const CenterWrap = styled.div`
  width: 100%;
`;

const Content = styled.div`
  flex: 1;
`;

const FormContent = styled.div`
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  /* Reserve space below the last field: a flex-column scroll container doesn't
     reliably keep its own padding-bottom at the end of the overflow, so this
     inner padding guarantees the final section stays fully scrollable. */
  padding-bottom: 40px;

  [data-embedded] & {
    max-width: none;
    margin: 0;
    padding-bottom: 0;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  text-align: center;
  gap: 4px;
  min-height: 200px;
`;

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.div`
  margin-bottom: 8px;
`;

const CharCounter = styled.div`
  text-align: right;
  font-size: 11px;
  color: var(--ink-lighter);
  margin-top: 4px;
`;

const SearchContainer = styled.div`
  position: relative;
`;

const SearchDropdown = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  max-height: 360px;
  overflow-y: auto;
  overflow-x: hidden;
  background: var(--flora-popover-bg);
  border: 1px solid var(--flora-popover-border);
  border-radius: 12px;
  box-shadow: var(--box-shadow-big-soft);
  z-index: 10;
  margin-bottom: 4px;
`;

const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--sky-light);
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 4px;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: var(--flora-popover-hover);
  }
`;

const DropdownBody = styled.div`
  padding: 4px;
`;

const DropdownSection = styled.div`
  padding: 4px;

  &:not(:last-child) {
    border-bottom: 1px solid var(--sky-light);
  }
`;

const SectionLabel = styled.div`
  padding: 6px 10px 4px;
`;

const NoResults = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const CategoryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 8px;
  border: none;
  border-radius: 8px;
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: var(--flora-popover-hover);
  }
`;

const CategoryIconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  flex-shrink: 0;
`;

const CategoryContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CategorySubtitle = styled.span`
  font-size: 11px;
  color: var(--ink-lighter);
`;

const ItemButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 6px;
  background: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: var(--flora-popover-hover);
  }
`;

const ItemContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.span`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-darkest);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ItemParent = styled.span`
  display: block;
  font-size: 11px;
  color: var(--ink-lighter);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SelectedObjectsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 12px;
`;

const SelectedObjectRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--sky-lightest);
  border: 1px solid var(--sky-light);
`;

const SelectedObjectInfo = styled.div`
  flex: 1;
  min-width: 0;
  line-height: 1.3;
`;

const RemoveButton = styled(ActionIcon)`
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  flex-shrink: 0;

  &:hover {
    background: var(--flora-hover-subtle);
  }
`;
